// Perch points (Phase 5): where a butterfly can land. Position plus surface
// normal, in the calibrated world frame. Placed by tapping the feed (the tap
// ray is intersected with a horizontal plane at a slider-set height), then
// nudged with sliders. Persisted to localStorage and exported as JSON for
// src/data/perches.json, which is the fallback when localStorage is empty.
import * as THREE from 'three';
import { config } from '../config.js';
import { storage } from '../util/storage.js';
import { el, button, slider } from '../debug/controls.js';
import fileDefault from '../data/perches.json';
import { saveToRepoButton } from '../debug/saveToRepo.js';

const KEY = 'mw.perches.v1';
const UP = new THREE.Vector3(0, 1, 0);
const COLOR = 0xff8800;
const COLOR_SELECTED = 0x00ffcc;

const clone = (list) => list.map((p) => ({ ...p, position: [...p.position], normal: [...p.normal] }));

// Normal <-> (tilt from +Y, azimuth around Y from +X toward +Z), degrees.
function anglesFromNormal([x, y, z]) {
  const tilt = THREE.MathUtils.radToDeg(Math.acos(Math.max(-1, Math.min(1, y))));
  const azimuth = Math.abs(x) < 1e-6 && Math.abs(z) < 1e-6 ? 0 : THREE.MathUtils.radToDeg(Math.atan2(z, x));
  return { tilt, azimuth };
}
function normalFromAngles(tilt, azimuth) {
  const t = THREE.MathUtils.degToRad(tilt);
  const a = THREE.MathUtils.degToRad(azimuth);
  return [Math.sin(t) * Math.cos(a), Math.cos(t), Math.sin(t) * Math.sin(a)];
}

export function createPerches({ scene, camera, canvas, overlay, aids, log = () => {} }) {
  const saved = storage.get(KEY);
  const list = Array.isArray(saved) && saved.length ? saved : clone(fileDefault);
  let selectedId = list[0]?.id ?? null;
  let placing = false;
  let placeHeight = config.perches.placeHeightDefault;
  let fine = false;
  let lastPicked = null;

  // Markers
  const markers = new THREE.Group();
  markers.name = 'perch-markers';
  scene.add(markers);
  const sphereGeo = new THREE.SphereGeometry(config.perches.markerSize / 2, 12, 8);

  function rebuildMarkers() {
    markers.clear();
    for (const p of list) {
      const selected = p.id === selectedId;
      const color = selected ? COLOR_SELECTED : COLOR;
      const sphere = new THREE.Mesh(sphereGeo, new THREE.MeshBasicMaterial({ color, depthTest: false }));
      sphere.position.fromArray(p.position);
      const arrow = new THREE.ArrowHelper(
        new THREE.Vector3().fromArray(p.normal).normalize(),
        sphere.position,
        config.perches.markerSize * 3,
        color,
        config.perches.markerSize,
        config.perches.markerSize * 0.6,
      );
      arrow.line.material.depthTest = false;
      arrow.cone.material.depthTest = false;
      markers.add(sphere, arrow);
    }
  }

  const selected = () => list.find((p) => p.id === selectedId) ?? null;
  const save = () => storage.set(KEY, list);
  const nextId = () => {
    let n = 1;
    while (list.some((p) => p.id === `p${n}`)) n++;
    return `p${n}`;
  };

  function toJSON() {
    return JSON.stringify(
      list.map((p) => ({
        id: p.id,
        position: p.position.map((v) => Number(v.toFixed(4))),
        normal: p.normal.map((v) => Number(v.toFixed(4))),
        label: p.label ?? '',
      })),
      null,
      2,
    );
  }

  // Tap to place: ray from the tap, intersected with the plane y = placeHeight.
  const raycaster = new THREE.Raycaster();
  const plane = new THREE.Plane(UP, 0);
  const hit = new THREE.Vector3();
  canvas.addEventListener('pointerdown', (e) => {
    if (!placing) return;
    const rect = canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
    raycaster.setFromCamera(ndc, camera);
    plane.constant = -placeHeight;
    if (!raycaster.ray.intersectPlane(plane, hit)) {
      log('tap did not hit the placement plane (looking away from it?)');
      return;
    }
    const perch = { id: nextId(), position: [hit.x, hit.y, hit.z], normal: [0, 1, 0], label: '' };
    list.push(perch);
    selectedId = perch.id;
    save();
    rebuildMarkers();
    refreshPanel();
    log(`perch ${perch.id} placed at ${perch.position.map((v) => v.toFixed(3)).join(' ')}`);
  });

  // Panel
  const section = overlay.section('Perches');
  const steps = () => (fine ? config.calibration.stepFine : config.calibration.stepCoarse);
  const normalStep = () => (fine ? config.perches.normalStepFine : config.perches.normalStepCoarse);

  const placeButton = button('Place: tap the feed', () => setPlacing(!placing));
  function setPlacing(on) {
    placing = on;
    placeButton.classList.toggle('on', on);
    if (on) overlay.minimize(`Placing perches at y=${placeHeight.toFixed(3)} m. Tap the feed. Tap here to stop.`, () => setPlacing(false));
  }

  const listEl = el('div', { class: 'mw-list' });
  const exportBox = el('textarea', { readonly: '', spellcheck: 'false' });
  exportBox.hidden = true;
  const fineButton = button('Fine steps', () => {
    fine = !fine;
    fineButton.classList.toggle('on', fine);
    sliders.forEach((s) => s.refreshStep());
  });

  const bar = el('div', { class: 'mw-bar' }, [
    placeButton,
    button('Add at origin', () => {
      const perch = { id: nextId(), position: [0, placeHeight, 0], normal: [0, 1, 0], label: '' };
      list.push(perch);
      selectedId = perch.id;
      save();
      rebuildMarkers();
      refreshPanel();
    }),
    button('Delete', () => {
      const i = list.findIndex((p) => p.id === selectedId);
      if (i < 0) return;
      list.splice(i, 1);
      selectedId = list[Math.min(i, list.length - 1)]?.id ?? null;
      save();
      rebuildMarkers();
      refreshPanel();
    }),
    button('Snap cube', () => {
      const p = selected();
      if (p) aids.snapCube(p);
    }),
    button('Cube home', () => aids.resetCube()),
    fineButton,
    button('Export JSON', async () => {
      exportBox.value = toJSON();
      exportBox.hidden = false;
      try {
        await navigator.clipboard.writeText(exportBox.value);
        log('perches JSON copied to clipboard');
      } catch {
        log('clipboard unavailable; select the text in the box');
      }
    }),
    button('Reset to file', () => {
      list.splice(0, list.length, ...clone(fileDefault));
      selectedId = list[0]?.id ?? null;
      save();
      rebuildMarkers();
      refreshPanel();
      log('perches reset to src/data/perches.json');
    }),
  ]);

  const heightSlider = slider({
    label: 'placement plane height (m)',
    min: -0.2,
    max: 0.5,
    step: () => steps().position,
    value: placeHeight,
    decimals: 3,
    onChange: (v) => (placeHeight = v),
  });

  const sliders = [heightSlider];
  const edit = (label, min, max, get, set, stepFn, decimals) => {
    const s = slider({
      label,
      min,
      max,
      step: stepFn,
      value: get(),
      decimals,
      onChange: (v) => {
        const p = selected();
        if (!p) return;
        set(p, v);
        save();
        rebuildMarkers();
        aids.snapCube(p);
        refreshList();
      },
    });
    sliders.push(s);
    return s;
  };
  const r = config.calibration.range.position;
  const posSliders = ['x', 'y', 'z'].map((axis, i) =>
    edit(`perch ${axis} (m)`, -r, r, () => selected()?.position[i] ?? 0, (p, v) => (p.position[i] = v), () => steps().position, 3),
  );
  const tiltSlider = edit(
    'normal tilt from up (deg)',
    0,
    180,
    () => (selected() ? anglesFromNormal(selected().normal).tilt : 0),
    (p, v) => (p.normal = normalFromAngles(v, anglesFromNormal(p.normal).azimuth)),
    normalStep,
    1,
  );
  const azimuthSlider = edit(
    'normal azimuth (deg)',
    -180,
    180,
    () => (selected() ? anglesFromNormal(selected().normal).azimuth : 0),
    (p, v) => (p.normal = normalFromAngles(anglesFromNormal(p.normal).tilt, v)),
    normalStep,
    1,
  );

  const saveButton = saveToRepoButton('perches', toJSON, log);
  if (saveButton) bar.append(saveButton);
  section.append(bar, heightSlider.root, listEl);
  for (const s of [...posSliders, tiltSlider, azimuthSlider]) section.append(s.root);
  section.append(exportBox);

  function refreshList() {
    listEl.replaceChildren(
      ...list.map((p) =>
        button(
          `${p.id}  (${p.position.map((v) => v.toFixed(3)).join(', ')})  n ${p.normal.map((v) => v.toFixed(2)).join(', ')}${p.label ? `  ${p.label}` : ''}`,
          () => {
            selectedId = p.id;
            rebuildMarkers();
            refreshPanel();
            aids.snapCube(p);
          },
          { class: p.id === selectedId ? 'on' : '' },
        ),
      ),
    );
  }

  function refreshPanel() {
    refreshList();
    const p = selected();
    posSliders.forEach((s, i) => s.set(p?.position[i] ?? 0));
    const a = p ? anglesFromNormal(p.normal) : { tilt: 0, azimuth: 0 };
    tiltSlider.set(a.tilt);
    azimuthSlider.set(a.azimuth);
  }

  overlay.row('Status', 'Perches', () => `${list.length}${saved ? '' : ' (file default)'}, selected ${selectedId ?? '-'}`);

  rebuildMarkers();
  refreshPanel();

  return {
    list,
    get selected() {
      return selected();
    },
    /** Random perch, never the same one twice in a row (Phase 7). */
    pick() {
      if (list.length === 0) return null;
      if (list.length === 1) return list[0];
      let p;
      do p = list[Math.floor(Math.random() * list.length)];
      while (p.id === lastPicked);
      lastPicked = p.id;
      return p;
    },
    setMarkersVisible(v) {
      markers.visible = v;
    },
    toJSON,
  };
}
